/** Os formatos (``video_type``) que cada factory registra, com o rótulo que o
 * usuário vê.
 *
 * Fonte única dos nomes: o formulário da factory e a lista de prioridade do
 * agendador precisam dizer a MESMA coisa. Antes disto os rótulos viviam só no
 * `FootballForm`, e a tela do agendador não tinha como mostrá-los.
 *
 * ⚠️ O backend também tem um nome para cada formato (``display_name`` no
 * ``register_video_type``), e ele é diferente — "Top 3 + Tabela" contra
 * "Última rodada". O que vale aqui é o rótulo do formulário, que é o que o
 * usuário reconhece.
 *
 * Factory sem entrada aqui é factory de formato único: não há o que ordenar.
 */
export interface VideoFormat {
  /** Slug do ``video_type`` no backend. É ele que viaja na API. */
  value: string
  label: string
}

/** Ordem = a prioridade editorial padrão do backend (``candidates.py``). */
export const FACTORY_VIDEO_FORMATS: Readonly<Record<string, ReadonlyArray<VideoFormat>>> = {
  football: [
    { value: 'top3_standings', label: 'Última rodada' },
    { value: 'next_round_preview', label: 'Próxima rodada' },
    { value: 'season_insight', label: 'Temporada' },
  ],
  // Card #145 balde 3: o APOD clássico NÃO grava video_type (D7) — o uploader
  // o enxerga pelo fallback ``kind``, por isso o slug aqui é o da própria
  // factory. Default do dono (D7): clássico no topo. Os demais são os
  // ``video_type`` que o sky_event grava no apod_meta.
  nasa_apod: [
    { value: 'nasa_apod', label: 'APOD do dia' },
    { value: 'sky_launch', label: 'Lançamento' },
    { value: 'sky_asteroid', label: 'Asteroide' },
    { value: 'sky_meteor_shower', label: 'Chuva de meteoros' },
    { value: 'sky_moon', label: 'Lua' },
    { value: 'sky_season', label: 'Estações' },
    { value: 'sky_planet', label: 'Planeta visível' },
  ],
  // docs/plan-quiz-choice.md: a quiz ganhou o formato "choice" (dilemas sem
  // resposta certa). O ``quiz_meta`` grava ``video_type`` desde então; vídeos
  // de trivia salvos antes caem no fallback ``kind`` ("quiz") no uploader, e o
  // ``mergeWithKnownFormats`` preserva esse slug se ele já estiver salvo.
  quiz: [
    { value: 'trivia', label: 'Com resposta' },
    { value: 'choice', label: 'Escolha' },
  ],
}

export function videoFormatsFor(factory: string): ReadonlyArray<VideoFormat> {
  return FACTORY_VIDEO_FORMATS[factory] ?? []
}

/** Rótulo de um slug, caindo no próprio slug quando não conhecemos.
 *
 * O fallback importa: um formato salvo na config e depois removido do código
 * ainda aparece na lista, com o slug cru, em vez de sumir sem explicação. */
export function videoFormatLabel(factory: string, value: string): string {
  return videoFormatsFor(factory).find((f) => f.value === value)?.label ?? value
}

/** Liga ou desliga um formato, devolvendo a lista em ordem alfabética.
 *
 * A ordem fixa é o que mantém o "Sem alterações" honesto: desligar A e depois
 * B tem de dar a mesma lista que B e depois A, e religar um formato tem de
 * voltar exatamente ao que estava salvo. */
export function setFormatDisabled(disabled: ReadonlyArray<string>, slug: string, off: boolean): string[] {
  const next = new Set(disabled)
  if (off) next.add(slug)
  else next.delete(slug)
  return [...next].sort()
}

/** A ordem com os formatos desativados no fim, cada grupo na ordem em que estava.
 *
 * É o que o modal salva: o desativado não disputa posição com quem vai ao ar,
 * e quem o religar o encontra no fim da lista, não no meio. */
export function disabledLast(order: ReadonlyArray<string>, disabled: ReadonlyArray<string>): string[] {
  return [...order.filter((v) => !disabled.includes(v)), ...order.filter((v) => disabled.includes(v))]
}

/** A ordem salva, completada com os formatos que faltam.
 *
 * O usuário não precisa saber quais são todos os formatos — a lista chega
 * inteira. O que já estava salvo mantém a posição; o resto entra no fim, na
 * ordem canônica. Slug salvo que não existe mais é preservado, para que salvar
 * de novo não o apague em silêncio.
 */
export function mergeWithKnownFormats(factory: string, saved: ReadonlyArray<string>): string[] {
  const known = videoFormatsFor(factory).map((f) => f.value)
  const kept = saved.filter((v, i) => saved.indexOf(v) === i)
  return [...kept, ...known.filter((v) => !kept.includes(v))]
}

/** Move um item de posição, devolvendo uma lista nova.
 *
 * Índice fora do intervalo devolve a lista intacta em vez de estourar: os dois
 * chamadores (arrastar e as setas) conseguem produzir alvos inválidos —
 * arrastar para fora da lista, ou clicar na seta do primeiro item.
 */
export function reorder(list: ReadonlyArray<string>, from: number, to: number): string[] {
  if (from === to || from < 0 || from >= list.length || to < 0 || to >= list.length) {
    return [...list]
  }
  const next = [...list]
  const [item] = next.splice(from, 1)
  // `noUncheckedIndexedAccess` tipa o splice como `string | undefined`, mesmo
  // com o índice já validado acima. A guarda satisfaz o compilador sem mentir
  // sobre o que pode acontecer.
  if (item === undefined) return [...list]
  next.splice(to, 0, item)
  return next
}
